class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onDrawerAction.bind(this));
    this.setHeaderCartIconAccessibility();
    this.groupDuplicateItems();
    this.cartGroupingUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'cart-items') return;
      setTimeout(() => this.groupDuplicateItems());
    });
  }

  disconnectedCallback() {
    if (this.cartGroupingUnsubscriber) this.cartGroupingUnsubscriber();
  }

  onDrawerAction(event) {
    const groupAction = event.target.closest('[data-cart-group-action]');
    if (groupAction) {
      event.preventDefault();
      this.changeGroup(groupAction, groupAction.dataset.cartGroupAction);
      return;
    }

    const checkoutButton = event.target.closest('[data-cart-checkout]');
    if (checkoutButton) {
      event.preventDefault();
      this.submitCheckout(checkoutButton);
      return;
    }

    const addButton = event.target.closest('[data-cart-add-variant]');
    if (addButton) {
      event.preventDefault();
      this.addVariant(addButton.dataset.cartAddVariant, addButton);
      return;
    }

    const upsellNavigation = event.target.closest('[data-cart-upsell-action]');
    if (upsellNavigation) {
      event.preventDefault();
      this.moveCrossSell(upsellNavigation.dataset.cartUpsellAction);
      return;
    }

    const addOnToggle = event.target.closest('[data-cart-addon-variant]');
    if (addOnToggle) {
      event.preventDefault();
      const isActive = addOnToggle.dataset.cartAddonActive === 'true';
      if (isActive) {
        this.changeVariant(addOnToggle.dataset.cartAddonVariant, 0, addOnToggle);
      } else {
        this.addVariant(addOnToggle.dataset.cartAddonVariant, addOnToggle);
      }
    }
  }

  submitCheckout(trigger) {
    if (trigger.disabled) return;

    const form = document.getElementById('CartDrawer-Form');
    if (form) {
      let checkoutInput = form.querySelector('input[name="checkout"]');
      if (!checkoutInput) {
        checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'checkout';
        form.appendChild(checkoutInput);
      }
      form.submit();
      return;
    }

    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    window.location.href = `${root}checkout`;
  }

  addVariant(variantId, trigger) {
    if (!variantId) return;
    this.setActionLoading(trigger, true);

    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => this.renderContents(parsedState))
      .catch(() => this.showActionError())
      .finally(() => this.setActionLoading(trigger, false));
  }

  changeVariant(variantId, quantity, trigger) {
    if (!variantId) return;
    this.setActionLoading(trigger, true);

    const body = JSON.stringify({
      id: String(variantId),
      quantity,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => this.renderContents(parsedState))
      .catch(() => this.showActionError())
      .finally(() => this.setActionLoading(trigger, false));
  }

  changeGroup(trigger, action) {
    const groupItem = trigger.closest('[data-cart-group-lines]');
    if (!groupItem || trigger.disabled) return;

    let lines;
    try {
      lines = JSON.parse(groupItem.dataset.cartGroupLines || '[]');
    } catch (_error) {
      this.showActionError();
      return;
    }
    if (!lines.length) return;

    const updates = {};
    if (action === 'remove') {
      lines.forEach((line) => { updates[line.key] = 0; });
    } else {
      const paidLines = lines.filter((line) => line.finalLinePrice > 0);
      const candidates = paidLines.length ? paidLines : lines;
      const line = action === 'plus' ? candidates[0] : candidates[candidates.length - 1];
      if (!line) return;
      updates[line.key] = action === 'plus' ? line.quantity + 1 : Math.max(0, line.quantity - 1);
    }

    this.setActionLoading(trigger, true);
    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => this.renderContents(parsedState))
      .catch(() => this.showActionError())
      .finally(() => this.setActionLoading(trigger, false));
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'nl-NL';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  }

  escapeHTML(value) {
    const element = document.createElement('span');
    element.textContent = String(value || '');
    return element.innerHTML;
  }

  groupDuplicateItems() {
    const items = Array.from(this.querySelectorAll('.balmelle-cart__item[data-cart-group]'));
    const groups = new Map();
    const cartPromotion = {
      eligibleQuantity: 0,
      bogoSavings: 0,
      bogoFreeQuantity: 0,
      otherSavings: 0,
      otherTitles: new Set(),
    };

    items.forEach((item) => {
      const key = item.dataset.cartGroup;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);

      const title = item.querySelector('.balmelle-cart__title')?.textContent?.trim() || '';
      const isEligibleRing = title.toLowerCase().includes('lip balm ring');
      const quantity = Number(item.dataset.cartLineQuantity || 0);
      const unitPrice = Number(item.dataset.cartUnitPrice || 0);
      let discounts = [];
      try {
        discounts = JSON.parse(item.dataset.cartLineDiscounts || '[]');
      } catch (_error) {
        discounts = [];
      }

      if (isEligibleRing) cartPromotion.eligibleQuantity += quantity;
      discounts.forEach((discount) => {
        const discountTitle = String(discount.title || '').trim();
        const amount = Number(discount.amount || 0);
        if (discountTitle.toLowerCase().includes('1+1') && isEligibleRing) {
          cartPromotion.bogoSavings += amount;
          if (unitPrice > 0) cartPromotion.bogoFreeQuantity += amount / unitPrice;
        } else if (discountTitle) {
          cartPromotion.otherSavings += amount;
          cartPromotion.otherTitles.add(discountTitle);
        }
      });
    });

    groups.forEach((group) => {
      const first = group[0];
      const lines = group.map((item) => ({
        key: item.dataset.cartLineKey,
        quantity: Number(item.dataset.cartLineQuantity || 0),
        originalLinePrice: Number(item.dataset.cartLineOriginal || 0),
        finalLinePrice: Number(item.dataset.cartLineFinal || 0),
        unitPrice: Number(item.dataset.cartUnitPrice || 0),
      }));
      const totalQuantity = lines.reduce((sum, line) => sum + line.quantity, 0);
      const originalTotal = lines.reduce((sum, line) => sum + line.originalLinePrice, 0);
      const finalTotal = lines.reduce((sum, line) => sum + line.finalLinePrice, 0);
      const unitPrice = lines.find((line) => line.unitPrice > 0)?.unitPrice || 0;
      const freeQuantity = unitPrice > 0 ? Math.round((originalTotal - finalTotal) / unitPrice) : 0;
      const price = first.querySelector('.balmelle-cart__price');
      const options = first.querySelector('.balmelle-cart__options');
      const details = first.querySelector('.balmelle-cart__details');
      const actions = first.querySelector('.balmelle-cart__actions');
      if (group.length === 1 && totalQuantity === 1) {
        const line = lines[0];
        if (line.originalLinePrice > 0 && line.finalLinePrice === 0) {
          if (price) {
            price.innerHTML = `<s>${this.formatMoney(line.originalLinePrice)}</s><span class="balmelle-cart__free-label">Gratis</span>`;
          }
          const summary = document.createElement('p');
          summary.className = 'balmelle-cart__group-summary';
          summary.innerHTML = '<strong>1 stuk</strong> · gratis';
          (options || price)?.insertAdjacentElement('afterend', summary);
        }
        return;
      }

      first.dataset.cartGroupLines = JSON.stringify(lines);
      if (price) {
        price.innerHTML = originalTotal !== finalTotal
          ? `<s>${this.formatMoney(originalTotal)}</s><span>${this.formatMoney(finalTotal)}</span>`
          : `<span>${this.formatMoney(finalTotal)}</span>`;
      }

      const summary = document.createElement('p');
      summary.className = 'balmelle-cart__group-summary';
      summary.innerHTML = `<strong>${totalQuantity} stuks</strong>${freeQuantity > 0 ? ` · waarvan ${freeQuantity} gratis` : ''}`;
      (options || price)?.insertAdjacentElement('afterend', summary);

      if (actions) {
        const discountList = group
          .map((item) => item.querySelector('.balmelle-cart__line-discounts'))
          .find((list) => list?.textContent?.trim());
        const discountMarkup = discountList?.outerHTML || '<span aria-hidden="true"></span>';
        actions.innerHTML = `
          ${discountMarkup}
          <div class="balmelle-cart__group-quantity" aria-label="Aantal aanpassen">
            <button type="button" data-cart-group-action="minus" aria-label="Eén ring minder">−</button>
            <output aria-live="polite">${totalQuantity}</output>
            <button type="button" data-cart-group-action="plus" aria-label="Eén ring meer">+</button>
          </div>
          <button type="button" class="balmelle-cart__group-remove" data-cart-group-action="remove" aria-label="Alle ${totalQuantity} ringen verwijderen">
            <span class="svg-wrapper"><svg viewBox="0 0 20 20" aria-hidden="true"><path fill="currentColor" d="M7 2h6l1 2h3v1H3V4h3l1-2Zm-2 4h10l-.7 12H5.7L5 6Zm3 2v7h1V8H8Zm3 0v7h1V8h-1Z"/></svg></span>
          </button>`;
      }

      group.slice(1).forEach((item) => item.remove());
      if (details) details.setAttribute('data-grouped-quantity', String(totalQuantity));
    });

    this.querySelector('.balmelle-cart__promotion-stack')?.remove();
    this.renderCrossSellRecommendations(items);
  }

  async renderCrossSellRecommendations(items) {
    const section = this.querySelector('.balmelle-cart__upsells');
    const list = section?.querySelector('.balmelle-cart__upsell-list');
    if (!section || !list) return;

    const cartProductIds = new Set(items.map((item) => String(item.dataset.cartProductId || '')).filter(Boolean));
    const sourceProductIds = Array.from(cartProductIds).slice(0, 4);
    const requestId = `${Date.now()}-${Math.random()}`;
    this.recommendationRequestId = requestId;
    section.hidden = true;
    list.innerHTML = '';
    if (!sourceProductIds.length) return;

    const root = window.Shopify?.routes?.root || '/';
    try {
      const recommendationSets = await Promise.all(
        sourceProductIds.map((productId) =>
          fetch(`${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=8&intent=related`)
            .then((response) => (response.ok ? response.json() : { products: [] }))
        )
      );
      if (this.recommendationRequestId !== requestId || !this.isConnected) return;

      const rankedProducts = new Map();
      recommendationSets.forEach((set) => {
        (set.products || []).forEach((product, index) => {
          const id = String(product.id || '');
          if (!id || cartProductIds.has(id) || !product.available) return;
          if (!String(product.title || '').toLowerCase().includes('lip balm ring')) return;
          const availableVariant = (product.variants || []).find((variant) => variant.available);
          if (!availableVariant) return;
          const existing = rankedProducts.get(id);
          const score = (existing?.score || 0) + 100 - index;
          rankedProducts.set(id, { product, variant: availableVariant, score });
        });
      });

      const maxProducts = Math.max(1, Math.min(Number(section.dataset.maxProducts || 4), 4));
      const suggestions = Array.from(rankedProducts.values())
        .sort((a, b) => b.score - a.score)
        .slice(0, maxProducts);
      if (!suggestions.length) return;

      list.innerHTML = suggestions.map(({ product, variant }) => {
        const title = this.escapeHTML(product.title);
        const handle = encodeURIComponent(product.handle || '');
        const imageUrl = this.escapeHTML(product.featured_image || product.images?.[0] || '');
        const image = imageUrl
          ? `<img class="balmelle-cart__upsell-image" src="${imageUrl}" alt="${title}" loading="lazy" width="58" height="58">`
          : '<span class="balmelle-cart__upsell-image" aria-hidden="true"></span>';
        return `
          <article class="balmelle-cart__upsell" data-product-handle="${handle}">
            <a class="balmelle-cart__upsell-media" href="${root}products/${handle}" tabindex="-1">${image}</a>
            <div class="balmelle-cart__upsell-info">
              <a href="${root}products/${handle}">${title}</a>
              <span>${this.formatMoney(Number(variant.price || product.price || 0))}</span>
            </div>
            <button type="button" class="balmelle-cart__upsell-button" data-cart-add-variant="${variant.id}" aria-label="${title} toevoegen">
              <span aria-hidden="true">+</span>
            </button>
          </article>`;
      }).join('');
      list.dataset.currentSlide = '0';
      list.addEventListener('scroll', () => {
        const cards = list.querySelectorAll('.balmelle-cart__upsell');
        const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : list.clientWidth;
        const index = Math.round(list.scrollLeft / Math.max(step, 1));
        this.updateCrossSellNavigation(index, suggestions.length);
      }, { passive: true });
      this.updateCrossSellNavigation(0, suggestions.length);
      section.hidden = false;
    } catch (_error) {
      section.hidden = true;
    }
  }

  moveCrossSell(action) {
    const list = this.querySelector('.balmelle-cart__upsell-list');
    const cards = list ? Array.from(list.querySelectorAll('.balmelle-cart__upsell')) : [];
    if (!list || cards.length < 2) return;
    const current = Number(list.dataset.currentSlide || 0);
    const next = action === 'previous'
      ? (current - 1 + cards.length) % cards.length
      : (current + 1) % cards.length;
    list.dataset.currentSlide = String(next);
    const targetLeft = cards[next].offsetLeft - cards[0].offsetLeft;
    list.scrollTo({ left: targetLeft, behavior: 'smooth' });
    this.updateCrossSellNavigation(next, cards.length);
  }

  updateCrossSellNavigation(index, total) {
    const section = this.querySelector('.balmelle-cart__upsells');
    const list = section?.querySelector('.balmelle-cart__upsell-list');
    const navigation = section?.querySelector('.balmelle-cart__upsell-navigation');
    const position = navigation?.querySelector('[data-cart-upsell-position]');
    if (!navigation || !position || !list) return;
    const safeIndex = Math.max(0, Math.min(index, total - 1));
    list.dataset.currentSlide = String(safeIndex);
    position.textContent = `${safeIndex + 1}/${total}`;
    navigation.hidden = total < 2;
  }

  renderPromotionSummary(promotion) {
    this.querySelector('.balmelle-cart__promotion-stack')?.remove();
    const itemList = this.querySelector('.balmelle-cart__items');
    if (!itemList || promotion.eligibleQuantity < 1) return;

    const stack = document.createElement('div');
    stack.className = 'balmelle-cart__promotion-stack';
    const roundedFreeQuantity = Math.round(promotion.bogoFreeQuantity);

    if (promotion.bogoSavings > 0 && roundedFreeQuantity > 0) {
      const grammar = roundedFreeQuantity === 1 ? 'is' : 'zijn';
      stack.insertAdjacentHTML('beforeend', `
        <aside class="balmelle-cart__promotion-panel" aria-label="1+1 gratis samenvatting">
          <span class="balmelle-cart__promotion-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path fill="currentColor" d="M3 12.6 12.6 3H21v8.4L11.4 21 3 12.6Zm14-5.1a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"/></svg>
          </span>
          <div class="balmelle-cart__promotion-copy">
            <strong>1+1 gratis toegepast</strong>
            <span>${roundedFreeQuantity} van de ${promotion.eligibleQuantity} ringen ${grammar} gratis</span>
            <span class="balmelle-cart__promotion-saving">Je bespaart ${this.formatMoney(promotion.bogoSavings)}</span>
          </div>
        </aside>`);
    }

    if (promotion.otherSavings > 0 && promotion.otherTitles.size > 0) {
      const title = this.escapeHTML(Array.from(promotion.otherTitles).join(' + '));
      stack.insertAdjacentHTML('beforeend', `
        <aside class="balmelle-cart__promotion-panel" aria-label="Andere korting samenvatting">
          <span class="balmelle-cart__promotion-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path fill="currentColor" d="M3 12.6 12.6 3H21v8.4L11.4 21 3 12.6Zm14-5.1a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"/></svg>
          </span>
          <div class="balmelle-cart__promotion-copy">
            <strong>${title} toegepast</strong>
            <span>Korting op je winkelwagen</span>
            <span class="balmelle-cart__promotion-saving">Je bespaart ${this.formatMoney(promotion.otherSavings)}</span>
          </div>
        </aside>`);
    }

    if (promotion.eligibleQuantity % 2 === 1) {
      const hasExistingFreeRing = roundedFreeQuantity > 0;
      stack.insertAdjacentHTML('beforeend', `
        <aside class="balmelle-cart__promotion-nudge" aria-label="Voeg nog een ring toe voor de 1+1 actie">
          <span class="balmelle-cart__promotion-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24"><path fill="currentColor" d="M20 7h-2.2c.1-.3.2-.7.2-1a3 3 0 0 0-5.6-1.5L12 5l-.4-.5A3 3 0 0 0 6 6c0 .3.1.7.2 1H4a2 2 0 0 0-2 2v2h9V9h2v2h9V9a2 2 0 0 0-2-2ZM9 7a1 1 0 1 1 1-1v1H9Zm6 0h-1V6a1 1 0 1 1 1 1ZM3 13h8v9H5a2 2 0 0 1-2-2v-7Zm10 0h8v7a2 2 0 0 1-2 2h-6v-9Z"/></svg>
          </span>
          <div class="balmelle-cart__promotion-copy">
            <strong>Voeg nog 1 ring toe — die wordt gratis</strong>
            <span>${hasExistingFreeRing ? 'Maak je volgende 1+1-set compleet.' : 'De tweede ring wordt automatisch gratis.'}</span>
            <a class="balmelle-cart__promotion-link" href="/collections/lippenbalsem-ringen">Kies nog een ring</a>
          </div>
        </aside>`);
    }

    if (stack.children.length) itemList.insertAdjacentElement('afterend', stack);
  }

  setActionLoading(trigger, isLoading) {
    if (!trigger) return;
    trigger.toggleAttribute('disabled', isLoading);
    trigger.classList.toggle('is-loading', isLoading);
  }

  showActionError() {
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.groupDuplicateItems();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
